import React, { useMemo } from 'react';
import { Alert, InputAdornment } from '@mui/material';
import {
    useContractDetail,
    useContractDetailData,
} from '../../../../../contextsProperty/ContextContractDetail';
import useLanguage from '../../../../../../customHooks/useLanguage';
import useTranslate from '../../../../../../customHooks/useTranslate';
import { PERSON_TYPES } from '../../../../../../types/entities/IStaticRateDateOccupancyRateParsed';
import Card from '../../../../../../muiComponents/Card';
import Grid from '../../../../../../muiComponents/Grid';
import GridChild from '../../../../../../muiComponents/GridChild';
import {
    gridSpacing20,
    gridSpacing30,
    singleLG_DoubleSM_ColumnGrid,
} from '../../../../../../utils/commonVariables/grid';
import AdultInputLabel from '../AdultInputLabel';
import TextInput from '../../../../../../muiComponents/TextInput/TextInput';
import Icon from '../../../../../../muiComponents/Icon';
import { AGE_SEGMENTS_ICONS } from '../../../../../../utils/contractUtils/contractUtils';
import { getOrdinalNumber } from '../../../../../../utils/usefulFunctions';
import useUserProfile from '../../../../../../customHooks/useUserProfile';

interface RatesByPersonProps {
    validationError: string;
}

interface IChildrenOccupancyRate {
    childIndex: number;
    ageSegments: number[];
}

const RatesByPerson = (props: RatesByPersonProps) => {
    const { validationError } = props;
    const { readOnlyUser } = useUserProfile();

    const {
        rateDateEdition,

        handleChangeRateDateEdition,
        isBlockedContract,
        isLoadingPanel,
    } = useContractDetail();
    const { ageSegments } = useContractDetailData();
    const language = useLanguage();
    const translate = useTranslate();

    const orderedAgeSegments = useMemo(() => {
        return ageSegments.sort((a, b) => {
            return a.age - b.age;
        });
    }, [ageSegments]);

    const childrenOccupancyRates = useMemo<IChildrenOccupancyRate[]>(() => {
        const rates = rateDateEdition?.occupancyRates?.persons?.rates || [];
        return rates
            .filter(rate => rate.personType === PERSON_TYPES.CHILD)
            .reduce<IChildrenOccupancyRate[]>((acc, rate) => {
                const person = acc.find(
                    item => item.childIndex === rate.personIndex
                );

                if (!person) {
                    acc.push({
                        childIndex: rate.personIndex,
                        ageSegments: [rate.ageSegment],
                    });
                } else if (!person.ageSegments.includes(rate.ageSegment)) {
                    person.ageSegments.push(rate.ageSegment);
                }

                return acc;
            }, []);
    }, [rateDateEdition?.occupancyRates?.persons?.rates]);

    return (
        <>
            <Alert severity='info' className='margin-top-10'>
                {translate('total_cost_by_person')}
            </Alert>
            {validationError ? (
                <Alert
                    severity='error'
                    className='rates-panel__validation-bar'
                    icon='ReportProblem'
                >
                    {translate(validationError)}
                </Alert>
            ) : (
                ''
            )}
            <div className='flex-column gap-20 margin-top-10'>
                <Card className='padding20 flex-column gap-20'>
                    <h3
                        className='contract-panel__title'
                        style={{ fontSize: '18px' }}
                    >
                        {translate('adults')}
                    </h3>
                    <Grid
                        columnSpacing={gridSpacing30}
                        rowSpacing={gridSpacing20}
                    >
                        {rateDateEdition?.occupancyRates?.persons?.rates
                            ?.filter(
                                rate => rate.personType === PERSON_TYPES.ADULT
                            )
                            .map(rate => {
                                const isBaseOccupancy = rate.personIndex === 1;
                                const isSingleUse =
                                    rate.personIndex === 0 &&
                                    rateDateEdition.occupancyRates.persons
                                        .singleUseAllowed;
                                const personNumber = isSingleUse
                                    ? 1
                                    : isBaseOccupancy
                                    ? rateDateEdition.standardOccupancy
                                    : rate.personIndex;
                                return (
                                    <GridChild
                                        key={rate.personType + rate.personIndex}
                                        gridProps={singleLG_DoubleSM_ColumnGrid}
                                    >
                                        <div
                                            key={
                                                rate.personType +
                                                rate.personIndex
                                            }
                                            className='text-seconday-700 flex-column flex-center gap-20'
                                        >
                                            <AdultInputLabel
                                                isBaseOccupancy={
                                                    isBaseOccupancy
                                                }
                                                isSingleUse={isSingleUse}
                                                personNumber={personNumber}
                                                standardOccupancy={
                                                    rateDateEdition.standardOccupancy
                                                }
                                            />
                                            <TextInput
                                                labelText='rate'
                                                type={'number'}
                                                deleteIcon={false}
                                                inputProps={{
                                                    step: 0.01,
                                                    min: 0,
                                                }}
                                                value={rate.abt}
                                                disabled={
                                                    isLoadingPanel ||
                                                    isBlockedContract ||
                                                    readOnlyUser
                                                }
                                                startAdornment={
                                                    !isSingleUse &&
                                                    !isBaseOccupancy ? (
                                                        <InputAdornment position='start'>
                                                            <span
                                                                style={{
                                                                    marginLeft:
                                                                        '10px',
                                                                    fontSize:
                                                                        '16px',
                                                                    color: 'var(--text)',
                                                                }}
                                                            >
                                                                +
                                                            </span>
                                                        </InputAdornment>
                                                    ) : undefined
                                                }
                                                handleChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    const value = parseFloat(
                                                        e?.target?.value
                                                    );
                                                    const newRate = {
                                                        ...rateDateEdition,

                                                        occupancyRates: {
                                                            ...rateDateEdition.occupancyRates,
                                                            persons: {
                                                                ...rateDateEdition
                                                                    .occupancyRates
                                                                    .persons,
                                                                rates: rateDateEdition.occupancyRates.persons.rates.map(
                                                                    newrate => {
                                                                        const isTargetRate =
                                                                            newrate.personType ===
                                                                                PERSON_TYPES.ADULT &&
                                                                            newrate.personIndex ===
                                                                                rate.personIndex;
                                                                        if (
                                                                            isTargetRate
                                                                        ) {
                                                                            return {
                                                                                ...newrate,
                                                                                abt: value,
                                                                                aat: value,
                                                                            };
                                                                        }
                                                                        return newrate;
                                                                    }
                                                                ),
                                                            },
                                                        },
                                                        __isChanged: true,
                                                    };
                                                    handleChangeRateDateEdition(
                                                        newRate
                                                    );
                                                }}
                                            />
                                        </div>
                                    </GridChild>
                                );
                            })}
                    </Grid>
                </Card>

                {orderedAgeSegments.length && !rateDateEdition.adultsOnly ? (
                    <Card
                        style={{
                            padding: '20px',
                            overflowX: 'auto',
                        }}
                    >
                        <h3
                            className='contract-panel__title'
                            style={{ fontSize: '18px' }}
                        >
                            {translate('children')}
                        </h3>

                        <table className='width100perc'>
                            <thead>
                                <tr>
                                    <th></th>
                                    {orderedAgeSegments.map(seg => {
                                        return (
                                            <th
                                                key={seg.id}
                                                className='text-seconday-700'
                                                style={{
                                                    textAlign: 'center',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <div
                                                    className='width100perc flex-center gap-10 text-overflow-ellipsis'
                                                    style={{
                                                        padding: '15px',
                                                    }}
                                                >
                                                    <Icon
                                                        iconName={
                                                            AGE_SEGMENTS_ICONS[
                                                                seg.id as keyof typeof AGE_SEGMENTS_ICONS
                                                            ]
                                                        }
                                                    />
                                                    <p className='text-overflow-ellipsis'>
                                                        {seg.name}
                                                    </p>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {childrenOccupancyRates?.map(childRate => {
                                    return (
                                        <tr key={childRate.childIndex}>
                                            <td
                                                className='text-seconday-700'
                                                style={{ fontWeight: 600 }}
                                            >
                                                <div className='height-100-perc flex-center'>
                                                    <p>
                                                        {`${getOrdinalNumber(
                                                            childRate.childIndex,
                                                            language
                                                        )} ${translate(
                                                            'child'
                                                        )}`}
                                                    </p>
                                                </div>
                                            </td>
                                            {childRate.ageSegments.map(seg => {
                                                const rate =
                                                    rateDateEdition?.occupancyRates?.persons?.rates?.find(
                                                        tarRate =>
                                                            tarRate.personType ===
                                                                PERSON_TYPES.CHILD &&
                                                            tarRate.ageSegment ===
                                                                seg &&
                                                            tarRate.personIndex ===
                                                                childRate.childIndex
                                                    );

                                                return (
                                                    <td
                                                        style={{
                                                            padding: '15px',
                                                        }}
                                                        key={seg}
                                                    >
                                                        <TextInput
                                                            labelText='rate'
                                                            type={'number'}
                                                            deleteIcon={false}
                                                            inputProps={{
                                                                step: 0.01,
                                                                m: 0,
                                                            }}
                                                            value={rate?.abt}
                                                            disabled={
                                                                isLoadingPanel ||
                                                                isBlockedContract ||
                                                                readOnlyUser
                                                            }
                                                            onChange={(
                                                                e: React.ChangeEvent<HTMLInputElement>
                                                            ) => {
                                                                const value =
                                                                    parseFloat(
                                                                        e
                                                                            ?.target
                                                                            ?.value
                                                                    );
                                                                const newRate =
                                                                    {
                                                                        ...rateDateEdition,

                                                                        occupancyRates:
                                                                            {
                                                                                ...rateDateEdition.occupancyRates,
                                                                                persons:
                                                                                    {
                                                                                        ...rateDateEdition
                                                                                            .occupancyRates
                                                                                            .persons,
                                                                                        rates: rateDateEdition.occupancyRates.persons.rates.map(
                                                                                            newrate => {
                                                                                                const isTargetRate =
                                                                                                    newrate.personType ===
                                                                                                        PERSON_TYPES.CHILD &&
                                                                                                    newrate.personIndex ===
                                                                                                        rate?.personIndex &&
                                                                                                    newrate.ageSegment ===
                                                                                                        rate?.ageSegment;
                                                                                                if (
                                                                                                    isTargetRate
                                                                                                ) {
                                                                                                    return {
                                                                                                        ...newrate,
                                                                                                        abt: value,
                                                                                                        aat: value,
                                                                                                    };
                                                                                                }
                                                                                                return newrate;
                                                                                            }
                                                                                        ),
                                                                                    },
                                                                            },
                                                                        __isChanged:
                                                                            true,
                                                                    };
                                                                handleChangeRateDateEdition(
                                                                    newRate
                                                                );
                                                            }}
                                                            startAdornment={
                                                                <InputAdornment position='start'>
                                                                    <span
                                                                        style={{
                                                                            marginLeft:
                                                                                '10px',
                                                                            fontSize:
                                                                                '16px',
                                                                            color: 'var(--text)',
                                                                        }}
                                                                    >
                                                                        +
                                                                    </span>
                                                                </InputAdornment>
                                                            }
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                ) : (
                    ''
                )}
            </div>
        </>
    );
};

export default RatesByPerson;
